import { Plus } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { BroadcastEditorDialog } from '@/features/broadcasts/BroadcastEditorDialog';
import type { Broadcast } from '@/features/broadcasts/broadcasts-types';
import { BroadcastsTable } from '@/features/broadcasts/BroadcastsTable';
import { SendBroadcastDialog } from '@/features/broadcasts/SendBroadcastDialog';
import {
  useBroadcastCohorts,
  useBroadcasts,
  useSendBroadcast,
} from '@/features/broadcasts/useBroadcasts';
import { ExperimentNotices } from '@/features/im-channel-proactivity-experiments/ExperimentNotices';
import type {
  ProactivenessCohortChannelFilter,
  ProactivenessFilter,
  ProactivenessSection,
} from '@/features/im-channel-proactivity-experiments/im-channel-proactivity-experiments-types';
import { RolloutEditorDialog } from '@/features/im-channel-proactivity-experiments/RolloutEditorDialog';
import {
  type CohortChannelFilters,
  type CohortFlagFilters,
  type CohortPages,
  useProactivenessRollouts,
} from '@/features/im-channel-proactivity-experiments/useImChannelProactivityExperiments';

export const BroadcastsPage = ({
  section,
  onSectionChange,
}: {
  section: ProactivenessFilter;
  onSectionChange: (section: ProactivenessFilter) => void;
}) => {
  const [pages, setPages] = useState<CohortPages>({
    experimental: 1,
    production: 1,
  });
  const [channelFilters, setChannelFilters] = useState<CohortChannelFilters>({
    experimental: 'all',
    production: 'all',
  });
  const [flagFilters, setFlagFilters] = useState<CohortFlagFilters>({
    experimental: '',
    production: '',
  });
  const [editor, setEditor] = useState<{ broadcast?: Broadcast } | null>(null);
  const [sending, setSending] = useState<Broadcast | null>(null);
  const [rolloutDialogOpen, setRolloutDialogOpen] = useState(false);
  const broadcastsQuery = useBroadcasts(section);
  const cohortQuery = useBroadcastCohorts(
    section,
    pages,
    channelFilters,
    flagFilters
  );
  const rolloutsQuery = useProactivenessRollouts();
  const sendBroadcast = useSendBroadcast();
  const broadcasts = broadcastsQuery.data ?? [];
  const rollouts = (rolloutsQuery.data ?? []).filter(
    (rollout) => section === 'all' || rollout.section === section
  );

  const resetPage = (cohortSection: ProactivenessSection) =>
    setPages((current) => ({ ...current, [cohortSection]: 1 }));

  const handleSend = (scheduledLocalAt?: string) => {
    if (!sending) return;
    sendBroadcast.mutate({
      id: sending.id,
      scheduled_local_at: scheduledLocalAt,
    });
    setSending(null);
  };

  return (
    <>
      <Card>
        <CardHeader className="flex-row flex-wrap items-start justify-between gap-4 space-y-0">
          <section>
            <CardTitle>Broadcasts</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Send one exact message to a PostHog rollout.
            </p>
          </section>
          <section className="flex flex-wrap items-center justify-end gap-2">
            <Select
              aria-label="View"
              className="w-auto"
              value={section}
              onChange={(event) =>
                onSectionChange(event.target.value as ProactivenessFilter)
              }
            >
              <option value="all">All</option>
              <option value="production">Production</option>
              <option value="experimental">Experiments</option>
            </Select>
            <Button
              variant="outline"
              onClick={() => setRolloutDialogOpen(true)}
            >
              <Plus />
              New rollout
            </Button>
            <Button
              onClick={() => setEditor({})}
              disabled={rollouts.length === 0}
            >
              <Plus />
              New broadcast
            </Button>
          </section>
        </CardHeader>
        <CardContent>
          <BroadcastsTable
            broadcasts={broadcasts}
            isLoading={broadcastsQuery.isLoading}
            error={broadcastsQuery.error}
            sendingId={
              sendBroadcast.isPending ? sendBroadcast.variables?.id : undefined
            }
            onEdit={(broadcast) => setEditor({ broadcast })}
            onSend={setSending}
          />
          <div className="mt-4">
            <ExperimentNotices
              rolloutsError={rolloutsQuery.error}
              cohorts={cohortQuery.data}
              cohortError={cohortQuery.error}
              syncError={null}
              sendResult={sendBroadcast.data}
              sendError={sendBroadcast.error}
              pauseError={null}
              isCohortLoading={cohortQuery.isFetching}
              cohortChannelFilters={channelFilters}
              cohortFlagFilters={flagFilters}
              cohortDescription="Users appear when at least one broadcast rollout flag is enabled. The Flags column shows every match. Channels reflect current connections."
              sendFailureMessage="Queueing was not confirmed for these users. Some may still receive the message."
              onCohortPageChange={(cohortSection, page) =>
                setPages((current) => ({ ...current, [cohortSection]: page }))
              }
              onCohortChannelFilterChange={(
                cohortSection: ProactivenessSection,
                channel: ProactivenessCohortChannelFilter
              ) => {
                setChannelFilters((current) => ({
                  ...current,
                  [cohortSection]: channel,
                }));
                resetPage(cohortSection);
              }}
              onCohortFlagFilterChange={(cohortSection, flagKey) => {
                setFlagFilters((current) => ({
                  ...current,
                  [cohortSection]: flagKey,
                }));
                resetPage(cohortSection);
              }}
            />
          </div>
        </CardContent>
      </Card>
      {editor && (
        <BroadcastEditorDialog
          key={editor.broadcast?.id ?? 'new'}
          broadcast={editor.broadcast}
          rollouts={rollouts}
          onOpenChange={(open) => {
            if (!open) setEditor(null);
          }}
        />
      )}
      {sending && (
        <SendBroadcastDialog
          broadcast={sending}
          onConfirm={handleSend}
          onOpenChange={(open) => {
            if (!open) setSending(null);
          }}
        />
      )}
      {rolloutDialogOpen && (
        <RolloutEditorDialog
          section={section === 'all' ? 'production' : section}
          onOpenChange={setRolloutDialogOpen}
        />
      )}
    </>
  );
};
